import React from 'react';
import { Database, X, AlertTriangle } from 'lucide-react';

interface NotionDisconnectDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const NotionDisconnectDialog: React.FC<NotionDisconnectDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
}) => {
    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleBackdropClick}
        >
            <div className="bg-slate-800 border border-gray-600 rounded-lg shadow-xl max-w-md w-full mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-600">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-full bg-red-500/20">
                            <Database className="w-5 h-5 text-red-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">
                            Disconnect Notion
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 bg-transparent hover:bg-transparent hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-300 mb-4">
                        Are you sure you want to disconnect your Notion integration? 
                    </p>
                    <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                            <div className="p-1 bg-orange-500/20 rounded-full mt-0.5">
                                <AlertTriangle className="w-4 h-4 text-orange-400" />
                            </div>
                            <div>
                                <p className="text-orange-300 font-medium text-sm mb-1">
                                    What happens when you disconnect
                                </p>
                                <ul className="text-orange-200 text-sm space-y-1 list-disc list-outside pl-5">
                                    <li>Your current synced posts in Notion will stay as they are.</li>
                                    <li>No new posts will sync automatically.</li>
                                    <li>To start syncing again, you'll need to reconnect.</li>
                                    <li>If you disconnect, Instavault will create a new Notion page and database when you reconnect</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-600">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                        <Database className="w-4 h-4" />
                        <span>Disconnect</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

